import React, { useContext } from 'react';
import { FontIcon } from 'react-md';
import styled from 'styled-components';

import { useTheme } from '../../../components/Theme/ThemeContext';
import Button from '../../common/Button';
import AuthUserContext from '../../../components/Session/AuthUserContext';
import { getString } from '../../../services/api/store';

const Wrapper = styled.button`
    display: flex;
    padding: 16px 8px;
    cursor: pointer;
    border-radius: 8px;
    background-color: transparent;
    border: none;
    text-align: left;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const ImageContainer = styled.div`
    margin-right: 16px;
`;

const ImageWrapper = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background-color: #b3b3b3;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
`;

const StyledFontIcon = styled(FontIcon)`
    font-size: 24px !important;
    color: #ffffff !important;
`;

const Content = styled.div`
    margin: auto auto auto 0;
`;

const PrimaryText = styled.div`
    font-family: 'Cabin', sans-serif;
    font-size: 15px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 20px;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.87);
`;

const SecondaryText = styled.div`
    font-family: 'Roboto', sans-serif;
    font-size: 13px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 20px;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.87);
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TimeText = styled.div`
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.67;
    letter-spacing: normal;
    color: rgba(0, 0, 0, 0.54);
`;

const BadgeWrapper = styled.div`
    padding: 0 8px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Badge = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${props => props.color};
`;

const ButtonsWrapper = styled.div`
    display: flex;
    margin: 8px 0;

    & > *:not(:last-child) {
        margin-right: 8px;
    }
`;

const NotificationItem = ({
    primaryText,
    secondaryText,
    timeText,
    imageUrl,
    icon,
    badge,
    onClick,
    buttons = [],
}) => {
    const { theme } = useTheme();
    const { authUser } = useContext(AuthUserContext);

    const imageOrIcon = imageUrl ? (
        <ImageWrapper>
            <Image src={imageUrl} alt="Notification" />
        </ImageWrapper>
    ) : (
        <ImageWrapper>
            <StyledFontIcon aria-hidden={'true'} iconClassName="material-icons-outlined">
                {icon}
            </StyledFontIcon>
        </ImageWrapper>
    );

    let subtitle = secondaryText;
    if (secondaryText?.includes('Requested a meeting')) {
        subtitle = secondaryText.replace(
            'Requested a meeting',
            getString('appointmentScheduledNotification', 'Requested a meeting'),
        );
    }
    if (secondaryText?.includes('Accepted your appointment')) {
        subtitle = secondaryText.replace(
            'Accepted your appointment',
            getString('appointmentAcceptedNotification', 'Accepted your appointment'),
        );
    }
    if (secondaryText?.includes('Declined your appointment')) {
        subtitle = secondaryText.replace(
            'Declined your appointment',
            getString('appointmentDeclinedNotification', 'Declined your appointment'),
        );
    }
    if (secondaryText?.includes('Accepted your contact request')) {
        subtitle = secondaryText.replace(
            'Accepted your contact request',
            getString('notificationAcceptedContactRequest', 'Accepted your contact request'),
        );
    }

    return (
        <Wrapper onClick={onClick} role="listitem">
            <ImageContainer>{imageOrIcon}</ImageContainer>
            <Content>
                <PrimaryText>
                    {primaryText === 'You'
                        ? `${authUser.firstName} ${authUser.lastName}`
                        : primaryText}
                </PrimaryText>
                <SecondaryText>{subtitle}</SecondaryText>
                <TimeText>{timeText}</TimeText>
                {buttons.length > 0 && (
                    <ButtonsWrapper>
                        {buttons.map((button, index) => (
                            <Button
                                key={`notification-item-button-${index}`}
                                type={button.type}
                                text={button.text}
                                onClick={button.onClick}
                                background={button.background}
                            />
                        ))}
                    </ButtonsWrapper>
                )}
            </Content>
            {badge && (
                <BadgeWrapper>
                    <Badge color={theme.contrast} />
                </BadgeWrapper>
            )}
        </Wrapper>
    );
};

export default NotificationItem;
